import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})

export class ProductService {
  apiURL: string = environment.apiURL;

  constructor(private http: HttpClient) {}

  /**
   * Create product
   */
  createProduct(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/products`, data);
  }

  /**
   * Get list of products
   */
  getListOfProducts(data: {skip?: number, limit?: number, search?: string, sector?: string, productClass?: string, user?: string, fromDate?: string, toDate?: string, sortField?: string, sortValue?: string, onlySearchIncluded?: boolean, areMaterialsIncluded?: boolean}): Observable<any> {
    let queryParams = {};

    if (data.skip !== null && data.skip !== undefined) queryParams['skip'] = data.skip;
    if (data.limit !== null && data.skip !== undefined) queryParams['limit'] = data.limit;
    if (data.search) queryParams['search'] = data.search;
    if (data.sector) queryParams['sector'] = data.sector;
    if (data.user) queryParams['user'] = data.user;
    if (data.fromDate) queryParams['fromDate'] = data.fromDate;
    if (data.toDate) queryParams['toDate'] = data.toDate;
    if (data.sortValue) queryParams[data.sortField] = data.sortValue;
    if (data.productClass) queryParams['productClass'] = data.productClass;
    if (data.onlySearchIncluded) queryParams['onlySearchIncluded'] = data.onlySearchIncluded;
    if (data.areMaterialsIncluded) queryParams['areMaterialsIncluded'] = data.areMaterialsIncluded;

    let params = new HttpParams({ fromObject: queryParams});

    return this.http.get(`${this.apiURL}/products`, { params });
  }

  /**
   * Get product details
   */
  getProductDetails(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}`);
  }

  /**
   * Edit product
   */
  editProduct(data: any, productId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}`, data);
  }

  /**
   * Delete product
   */
  deleteProduct(productId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/products/${productId}`);
  }

  /**
   * Archive product
   */
   archiveProduct(productId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/archive`, {});
  }

  /**
   * Sync products with upis
   */
  syncProducts(): Observable<any> {
    return this.http.post(`${this.apiURL}/products/sync`, {});
  }

  /**
   * Get documents
   */
  getProductDocuments(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}/documents`);
  }

  /**
   * Add document
   */
  addDocument(data: FormData, productId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/products/${productId}/documents`, data);
  }

  /**
   * Edit document
   */
  editDocument(data: any, productId: string, documentId: number): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/documents/${documentId}`, data);
  }

  /**
   * Delete document
   */
  deleteDocument(productId: string, documentId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/products/${productId}/documents/${documentId}`);
  }

  /**
   * Download document
   */
  downloadDocument(documentId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/documents/${documentId}/single-download`);
  }

  /**
   * Download all documents
   */
  downloadAllDocuments(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}/documents/download`, {
      responseType: 'blob',
      observe: 'body'
    });
  }

  /**
   * Get product elements 
   */
  getProductElements(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}/bill-of-materials`);
  }

  /**
   * Add product element
   */
  addProductElement(data: any, productId: string, productPartId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/products/${productId}/product-parts/${productPartId}`, data);
  }

  /**
   * Edit product element
   */
  editProductElement(data: any, productId: string, semiProductId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/semi-products/${semiProductId}`, data);
  }

  /**
   * Delete product element
   */
  deleteProductElement(productId: string, semiProductId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/products/${productId}/semi-products/${semiProductId}`);
  }

  /**
   * Get operations
   */
  getOperations(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}/operations`);
  }

  /**
   * Add operation
   */
  createOperation(data: any, productId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/products/${productId}/operations`, data);
  }

  /**
   * Edit operation
   */
  editOperation(data: any, productId: string, productOperationId: number): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/operations/${productOperationId}`, data);
  }

  /**
   * Delete operation
   */
  deleteOperation(productId: string, productOperationId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/products/${productId}/operations/${productOperationId}`);
  }

  /**
   * Delete all operations
   */
  deleteAllOperations(productId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/products/${productId}/operations`);
  }

  /**
   * Change operations order
   */
  changeProductOperationsOrder(data: any, productId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/operations`, data);
  }

  /**
   * Import operation
   */
  addOperationToProduct(productId: string, operationId: string, data): Observable<any> {
    return this.http.post(`${this.apiURL}/products/${productId}/operations/${operationId}`, data);
  }


  /**
   * Add product sector
   */
  addProductSector(sector: any, productId): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/sectors`, sector);
  }

  /**
   * Get list od product sectors
   */
  getListOfProductSectors(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}/sectors`);
  }

  /**
   * Import bill of materials from product
   */
  copyProduct(productId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/products/${productId}/bill-of-materials/clone`, {});
  }

  /**
   * Approve document
   */
  approveDocument(productId: string, documentId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/documents/${documentId}/approve`, {});
  }

  /**
   * Download list of products
   */
  downloadListOfProducts(data: {search?: string, sector?: string, productClass?: string, user?: string, fromDate?: string, toDate?: string, sortField?: string, sortValue?: string, onlySearchIncluded?: boolean}): Observable<any> {
    let params = new HttpParams();

    if (data.search) params = params.append('search', data.search);
    if (data.sector) params = params.append('sector', data.sector);
    if (data.productClass) params = params.append('productClass', data.productClass);
    if (data.user) params = params.append('user', data.user);
    if (data.fromDate) params = params.append('fromDate', data.fromDate);
    if (data.toDate) params = params.append('toDate', data.toDate);
    if (data.sortField) params = params.append(data.sortField, data.sortValue);
    if (data.onlySearchIncluded) params = params.append('onlySearchIncluded', data.onlySearchIncluded);

    return this.http.get(`${this.apiURL}/products/download`, {
      params: params,
      responseType: 'blob',
      observe: 'body'
    });
  }

  /**
   * Upload operations
   */
  uploadOperations(data: FormData): Observable<any> {
    return this.http.post(`${this.apiURL}/operations/upload`, data);
  }

  /**
   * Get product materials
   */
  getProductMaterials(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}/materials`);
  }
  
  /** 
   * Download bill of materials
   */
  downloadBillOfMaterials(productId: string): Observable<any> {
    let params = new HttpParams();

    return this.http.get(`${this.apiURL}/products/${productId}/download-bom`, {
      params: params,
      responseType: 'blob',
      observe: 'body'
    });
  }

  /**
   * Get product assignees
   */
  getProductAssignees(productId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/products/${productId}/assignees`);
  }

  /**
   * Assign user to product
   */
   assignUserToProduct(productId: string, assigneeId: string, comment: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/assignees/${assigneeId}`, comment);
  }

  /**
   * Copy bill of materials from one product to another
   */
  copyBillOfMaterials(productId: string, fromProductId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/bill-of-materials/copy`, { fromProductId });
  }
  
  /**
   * Copy product operations
   */
  copyProductOperations(productId: string, fromProductId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/products/${productId}/operations/copy`, { fromProductId });
  }

  /**
   * Confirm bill of materials
   */
  confirmBillOfMaterials(productId: string, isConfirmed: boolean): Observable<any> {
    let params = new HttpParams();

    if (isConfirmed !== undefined || isConfirmed !== null) params = params.append('isConfirmed', isConfirmed);

    return this.http.patch(`${this.apiURL}/products/${productId}/bill-of-materials/confirm`, null, { params });
  }

  /**
   * Confirm product operations
   */
  confirmProductOperations(productId: string, isConfirmed: boolean): Observable<any> {
    let params = new HttpParams();

    if (isConfirmed !== undefined || isConfirmed !== null) params = params.append('isConfirmed', isConfirmed);

    return this.http.patch(`${this.apiURL}/products/${productId}/operations/confirm`, null, { params });
  }

  /**
   * Get list of products - reverse search
   */
  getContainingProducts(productId: string, skip?: number, limit?: number): Observable<any> {
    let params = new HttpParams()
    .set('skip', skip)
    .set('limit', limit);

    return this.http.get(`${this.apiURL}/products/${productId}/contains`, { params });
  }

  /**
   * Lock/Unlock product
   */
  toggleProductLock(productId: string, lock: boolean): Observable<any> {
    let params = new HttpParams();

    if (lock) params = params.append('lock', lock);
    return this.http.patch(`${this.apiURL}/products/${productId}/unlock`, null, { params });
  }
}